import { createTheme, Theme } from "@mui/material/styles";
import type { ThemeName } from "./context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// HARMONY (docs/26) — warm enterprise skin authored by EZ Cloud (no third-party
// tokens/assets). Values mirror the [data-theme="harmony"] CSS vars in index.css;
// keep the two in sync when iterating (Gate C feedback lands here + index.css).
// ─────────────────────────────────────────────────────────────────────────────
export const HARMONY = {
  bgDefault: "#F5F4F2", // warm stone canvas
  surface: "#FBFAF8", // cards
  surface2: "#F0EEEB",
  borderCard: "#E4E1DD",
  borderInput: "#C6C1BB",
  textPrimary: "#161513", // warm near-black, never pure #000
  textSecondary: "#5B5652",
  accent600: "#C5402F", // primary action ramp (ours)
  accent700: "#A63425",
  accent50: "#FBEDEA",
  charcoal: "#3A3631", // secondary accent — enterprise weight
  charcoalHover: "#262320",
  success: "#256B2B",
  danger: "#B42318",
  warning: "#8A5B00",
  info: "#6B46C1",
  blue: "#2B5FC2",
  shadow: "0 1px 4px rgba(22, 21, 19, 0.08)", // single soft elevation level
  fontFamily: '"Source Sans 3", Inter, system-ui, sans-serif',
} as const;

const harmonyTheme = (): Theme => {
  const base = createTheme({
    palette: {
      mode: "light",
      primary: { main: HARMONY.accent600, dark: HARMONY.accent700, light: HARMONY.accent50 },
      secondary: { main: HARMONY.charcoal, dark: HARMONY.charcoalHover },
      success: { main: HARMONY.success },
      error: { main: HARMONY.danger },
      warning: { main: HARMONY.warning },
      info: { main: HARMONY.info },
      background: { default: HARMONY.bgDefault, paper: HARMONY.surface },
      text: { primary: HARMONY.textPrimary, secondary: HARMONY.textSecondary },
      divider: HARMONY.borderCard,
    },
    typography: {
      fontFamily: HARMONY.fontFamily,
      fontSize: 14,
      h1: { fontWeight: 600, letterSpacing: "-0.02em" },
      h2: { fontWeight: 600, letterSpacing: "-0.02em" },
      h3: { fontWeight: 600, letterSpacing: "-0.015em" },
      h4: { fontWeight: 600, letterSpacing: "-0.015em" },
      h5: { fontWeight: 600, letterSpacing: "-0.01em" },
      h6: { fontWeight: 600, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    shape: { borderRadius: 6 }, // cards 6px; inputs/buttons pinned to 4px below
  });

  return createTheme(base, {
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 4, textTransform: "none" as const, boxShadow: "none" },
          contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 4, backgroundColor: "#FDFCFA" },
          notchedOutline: { borderColor: HARMONY.borderInput },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          // Borders over shadows: flat, calm surfaces (docs/26 elevation token)
          elevation1: { boxShadow: HARMONY.shadow, border: `1px solid ${HARMONY.borderCard}` },
          elevation2: { boxShadow: HARMONY.shadow, border: `1px solid ${HARMONY.borderCard}` },
          elevation3: { boxShadow: HARMONY.shadow, border: `1px solid ${HARMONY.borderCard}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            border: `1px solid ${HARMONY.borderCard}`,
            boxShadow: HARMONY.shadow,
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 6, boxShadow: "0 8px 24px rgba(22,21,19,.14)" } },
      },
      MuiTableCell: {
        styleOverrides: {
          // Enterprise data density: 13px cells, row height ~ -15% vs Light
          root: { fontSize: 13, padding: "9px 14px", borderBottomColor: HARMONY.borderCard },
          head: { fontWeight: 600, color: HARMONY.textSecondary },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 4, fontWeight: 600 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: HARMONY.charcoal, fontSize: 12, borderRadius: 4 },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: "none" as const, fontWeight: 600 } },
      },
    },
  });
};

export const getTheme = (theme: ThemeName | boolean): Theme => {
  // Back-compat: a boolean means the old isDark flag.
  const name: ThemeName = typeof theme === "boolean" ? (theme ? "dark" : "light") : theme;

  if (name === "harmony") return harmonyTheme();

  if (name === "dark") {
    return createTheme({
      palette: {
        mode: "dark",
        primary: {
          main: "#EC7C37",
        },
        background: {
          default: "#0f1117",
          paper: "#1a1d27",
        },
        text: {
          primary: "#e8eaf0",
          secondary: "#6b7280",
        },
        divider: "rgba(255,255,255,0.12)",
      },
      typography: {
        fontFamily: "Montserrat, sans-serif",
      },
    });
  }

  // Light: original empty theme — no custom primary/background/text overrides.
  // MUI defaults are used (blue primary #1976d2, white paper, etc.)
  return createTheme({
    typography: {
      fontFamily: "Montserrat, sans-serif",
    },
  });
};

// Legacy default export — keeps any direct imports working
export default getTheme("light");
